import { useMonth } from '@datepicker-react/hooks';
import { faAngleLeft, faAngleRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { add, setMonth, setYear } from 'date-fns';
import { getTimezoneOffset } from 'date-fns-tz';
import type { FC } from 'react';
import { Button } from 'src/components/buttons-and-actions/button';
import { Day, type IsDayDragging } from 'src/components/form/datepicker/day';
import type { FormOption } from 'src/components/form/form-option';
import { SimpleSelect } from 'src/components/form/select';
import { Stack } from 'src/components/layout/stack';
import { cn } from 'src/utils/cn';

const toggleButtonClasses = cn(`
  border-none! p-0! text-foreground-secondary-default
  not-legacy:text-foreground-secondary-default!
  legacy:pr-[1.5rem]!
`);

const selectClasses = cn(`
  [&_[data-addon-wrapper]]:not-legacy:hidden
  [&_[data-addon-wrapper]]:legacy:pr-0
  [&_[data-addon-wrapper]]:legacy:pl-[0.5rem]
  [&_button[role="combobox"]]:not-legacy:cursor-pointer!
`);

type MonthProps = {
  year: number;
  month: number;
  date: Date;
  monthOffset?: number;
  startDate: Date | null;
  endDate: Date | null;
  /**
   * Either a hovered date or dragged over date.
   */
  targetDate: Date | null;
  isDayDragging: IsDayDragging;
  hideNextMonthButton?: boolean;
  hidePrevMonthButton?: boolean;
  onChange: (date: Date) => void;
  onDateSelect: (date: Date) => void;
  onNextMonthClick: () => void;
  onPrevMonthClick: () => void;
  onDayHover: (date: Date | null) => void;
  isDateBlocked: (date: Date) => boolean;
};

export const Month: FC<MonthProps> = ({
  year,
  month,
  date,
  monthOffset = 0,
  startDate,
  endDate,
  targetDate,
  hideNextMonthButton = false,
  hidePrevMonthButton = false,
  onChange,
  onNextMonthClick,
  onPrevMonthClick,
  onDateSelect,
  onDayHover,
  isDateBlocked,
  isDayDragging,
}) => {
  const { days, weekdayLabels } = useMonth({
    year,
    month,
    weekdayLabelFormat(date) {
      return date.toLocaleDateString('en-GB', { weekday: 'short' });
    },
  });

  return (
    <div
      className={`
        w-[28rem]
        not-legacy:grid not-legacy:gap-xs
      `}
    >
      <Stack templateColumns="3rem auto 3rem" justifyContent="space-between">
        {hidePrevMonthButton ? (
          <span />
        ) : (
          <Button
            mode="icon"
            onClick={onPrevMonthClick}
            className={`
              text-[2rem]
              legacy:text-[3rem] legacy:leading-[1.15]
            `}
          >
            <FontAwesomeIcon icon={faAngleLeft} aria-label="Prev month" />
          </Button>
        )}

        <div className="flex gap-xl">
          <SimpleSelect
            name="month"
            value={month.toString()}
            options={months}
            onChange={(month) =>
              onChange(setMonth(date, Number(month) - monthOffset))
            }
            sortBy="manual"
            toggleButtonClassName={toggleButtonClasses}
            className={selectClasses}
          />

          <SimpleSelect
            name="year"
            value={year.toString()}
            options={years}
            onChange={(year) => onChange(setYear(date, Number(year)))}
            toggleButtonClassName={toggleButtonClasses}
            className={selectClasses}
          />
        </div>

        {hideNextMonthButton ? (
          <span />
        ) : (
          <Button
            mode="icon"
            onClick={onNextMonthClick}
            className={`
              text-[2rem]
              legacy:text-[3rem] legacy:leading-[1.15]
            `}
          >
            <FontAwesomeIcon icon={faAngleRight} aria-label="Next month" />
          </Button>
        )}
      </Stack>

      <div className="relative">
        <div
          className={`
            grid grid-cols-7 justify-center font-body-semibold
            legacy:font-[900]
          `}
        >
          {weekdayLabels.map((dayLabel) => (
            <div key={dayLabel} className="p-[0.4rem] text-center">
              {dayLabel}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 justify-center">
          {days.map((day, index) => {
            if (typeof day === 'object') {
              // the dates generated are always 00:00 local time, we need this to be 00:00 in Europe/Berlin (CET/CEST)
              // this is not just timezone "conversion", but we need to find the difference in hours and subtract them
              // for this we have:
              //  - day.date.getTimezoneOffset() => offset from browser TZ to UTC
              //  - date-fns-tz's getTimezoneOffset(timeZone: String, date: Date|Number) => can be used to get current offset of Europe/Berlin
              const midnightInUTC = add(day.date, {
                minutes: -1 * day.date.getTimezoneOffset(),
              });
              const midnightInBerlin = add(midnightInUTC, {
                // function yields milliseconds, duration only accepts seconds
                seconds:
                  getTimezoneOffset('Europe/Berlin', midnightInUTC) / -1000,
              });

              // check if this date switches from CET to CEST or vice versa
              const nextDay = add(midnightInBerlin, { days: 1 });
              const currentOffset = getTimezoneOffset(
                'Europe/Berlin',
                midnightInBerlin
              );
              const nextDayOffset = getTimezoneOffset('Europe/Berlin', nextDay);

              const dstChange =
                currentOffset === nextDayOffset
                  ? 'REGULAR'
                  : currentOffset < nextDayOffset
                    ? 'CET_TO_CEST'
                    : 'CEST_TO_CET';

              return (
                <Day
                  key={day.dayLabel}
                  date={midnightInBerlin}
                  dayLabel={day.dayLabel}
                  startDate={startDate}
                  endDate={endDate}
                  targetDate={targetDate}
                  isDayDragging={isDayDragging}
                  onDateSelect={onDateSelect}
                  onDayHover={onDayHover}
                  isDateBlocked={isDateBlocked}
                  dstChange={dstChange}
                />
              );
            }

            return <div key={index} />;
          })}
        </div>
      </div>
    </div>
  );
};

const months: FormOption[] = [
  { value: '0', label: 'January' },
  { value: '1', label: 'February' },
  { value: '2', label: 'March' },
  { value: '3', label: 'April' },
  { value: '4', label: 'May' },
  { value: '5', label: 'June' },
  { value: '6', label: 'July' },
  { value: '7', label: 'August' },
  { value: '8', label: 'September' },
  { value: '9', label: 'October' },
  { value: '10', label: 'November' },
  { value: '11', label: 'December' },
];

// 1900-2099
const years: FormOption[] = Array.from({ length: 200 }, (_, i) => i + 1900).map(
  (year) => ({
    value: year.toString(),
    label: year.toString(),
  })
);
